import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, SearchableSelect, useToast, type SearchableSelectOption } from '@kss/ui';
import { ArrowLeft, Save } from 'lucide-react';
import { categoryService, type Category, type CategoryCreate } from '@kss/api';
import { decodeId } from '../../utils/idCodec';

const inputCls = 'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const textareaCls = 'min-h-[80px] w-full rounded-md border border-border bg-surface-card px-3 py-2 text-sm text-content-primary outline-none focus:border-primary';
const selectCls = 'h-10 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

const loadCategoryOptions = async (): Promise<SearchableSelectOption[]> => {
  try {
    const rows = await categoryService.all();
    return (Array.isArray(rows) ? rows : [])
      .map((r) => ({
        value: r.categoryId ?? '',
        label: r.name ?? '(unnamed)',
        description: r.description,
      }))
      .filter((o) => o.value);
  } catch {
    return [];
  }
};

export default function NewCategory() {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = decodeId(rawId);
  const isEdit = !!id && id !== 'new';
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast); toastRef.current = toast;

  const [form, setForm] = useState({
    name: '',
    description: '',
    parentId: search.get('parent') ?? '',
    sortOrder: 0,
    isActive: true,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (isEdit && id) {
        setLoading(true); setErr(null);
        try {
          const r = await categoryService.get(id);
          if (cancelled) return;
          setForm({
            name: r.name ?? '',
            description: r.description ?? '',
            parentId: r.parentId ?? '',
            sortOrder: r.sortOrder ?? 0,
            isActive: !!r.isActive,
          });
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Not found';
          setErr(msg); toastRef.current.error('Load failed', msg);
        } finally { if (!cancelled) setLoading(false); }
      }
    }
    void run();
    return () => { cancelled = true; };
  }, [id, isEdit]);

  async function save() {
    if (!form.name.trim()) {
      toast.error('Fix errors', 'Name is required');
      return;
    }
    setSaving(true);
    try {
      const base = {
        name: form.name,
        description: form.description || undefined,
        parentId: form.parentId || undefined,
        sortOrder: form.sortOrder || undefined,
        isActive: form.isActive,
      };
      if (isEdit && id) {
        await categoryService.update([{ categoryId: id, ...base } as Category]);
        toast.success('Saved', `${form.name} updated`);
      } else {
        await categoryService.create([base as CategoryCreate]);
        toast.success('Saved', `${form.name} added`);
      }
      navigate('/panel/categories');
    } catch (e) {
      toast.error('Save failed', e instanceof Error ? e.message : 'Failed');
    } finally { setSaving(false); }
  }

  return (
    <div className="-m-6 p-[3px]">
      {err && <Alert variant="danger" className="mb-3">{err}</Alert>}
      <Card>
        <div className="mb-3 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/panel/categories')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-base font-semibold text-content-primary">
            {isEdit ? 'Edit category' : 'New category'}
          </h2>
        </div>
        {loading ? (
          <div className="text-sm text-content-muted">Loading…</div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">Name <span className="text-danger">*</span></span>
              <input className={inputCls} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Food / Indian / Punjabi" />
            </label>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">Parent</span>
              <SearchableSelect
                value={form.parentId}
                onChange={(v) => setForm((f) => ({ ...f, parentId: v }))}
                loadOptions={loadCategoryOptions}
                placeholder="Top-level"
                clearable
              />
            </div>
            <label className="flex flex-col gap-1 md:col-span-2">
              <span className="text-xs font-medium text-content-secondary">Description</span>
              <textarea className={textareaCls} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">Sort order</span>
              <input type="number" className={inputCls} value={form.sortOrder} onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">Status</span>
              <select className={selectCls} value={form.isActive ? 'active' : 'inactive'} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.value === 'active' }))}>
                <option value="active">active</option>
                <option value="inactive">inactive</option>
              </select>
            </label>
          </div>
        )}
        <div className="mt-4 flex items-center justify-end gap-2 border-t border-border pt-4">
          <Button variant="outline" onClick={() => navigate('/panel/categories')} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || loading}>
            <Save className="h-4 w-4" /> {saving ? 'Saving…' : isEdit ? 'Update' : 'Save'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
